import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { productsApi, productReviewsApi } from '../utils/apiClient';
import { useAuth } from '../contexts/AuthContext';
import Navbar from '../components/Navigation/Navbar';
import SciFiBackground from '../components/SciFiBackground/SciFiBackground';
import {
  Box,
  Container,
  Stack,
  Typography,
  Button,
  Grid,
  Paper,
  Chip,
  Rating,
  TextField,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  IconButton,
  Divider,
  Avatar,
  Tooltip,
  Skeleton,
  useTheme,
  FormControl,
  InputLabel,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import SendIcon from '@mui/icons-material/Send';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import { useTranslation } from 'react-i18next';

const ProductDetails = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { t } = useTranslation();
  const theme = useTheme();
  const borderRadius = '25px';
  const compactRadius = '15px';
  
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [reviews, setReviews] = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(true);
  const [stats, setStats] = useState({ totalReviews: 0, averageRating: 0 });
  const [myReview, setMyReview] = useState({ rating: '', comment: '' });
  const [submittingReview, setSubmittingReview] = useState(false);
  const [canReview, setCanReview] = useState(false);

  useEffect(() => {
    const fetchProduct = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await productsApi.getById(productId);
        if (data && data.success && data.data) {
          setProduct(data.data);
        } else {
          setError(t('productDetails.productNotFound'));
        }
      } catch (err) {
        setError(t('productDetails.fetchError'));
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [productId]);

  useEffect(() => {
    const loadReviews = async () => {
      setReviewsLoading(true);
      try {
        const [listRes, statsRes] = await Promise.all([
          productReviewsApi.list(productId),
          productReviewsApi.stats(productId)
        ]);
        if (listRes?.success) setReviews(listRes.data || []);
        if (statsRes?.success) setStats(statsRes.data || { totalReviews: 0, averageRating: 0 });
      } catch (e) {
        // ignore
      } finally {
        setReviewsLoading(false);
      }
    };
    loadReviews();
  }, [productId]);

  useEffect(() => {
    const check = async () => {
      if (!isAuthenticated) { setCanReview(false); return; }
      try {
        const res = await productReviewsApi.canReview(productId);
        setCanReview(!!res?.success && !!res?.data?.canReview);
      } catch (e) {
        setCanReview(false);
      }
    };
    check();
  }, [isAuthenticated, productId]);

  const formatPrice = (price) => `${new Intl.NumberFormat('en-US').format(price ?? 0)} TND`;

  const handleQuantityChange = (e) => {
    let val = parseInt(e.target.value, 10);
    if (isNaN(val) || val < 1) val = 1;
    setQuantity(val);
  };

  const handleBuyNow = () => {
    if (product && !product.soldOut && quantity > 0) {
      if (!isAuthenticated) {
        navigate('/auth');
        return;
      }
      navigate(`/payment/${product._id}`);
    }
  };

  const submitReview = async (e) => {
    e.preventDefault();
    if (!canReview) {
      return;
    }
    if (!myReview.rating) {
      return;
    }
    setSubmittingReview(true);
    try {
      const res = await productReviewsApi.upsert(productId, {
        rating: parseInt(myReview.rating, 10),
        comment: myReview.comment || ''
      });
      if (res?.success) {
        // Refresh reviews and stats
        const [listRes, statsRes] = await Promise.all([
          productReviewsApi.list(productId),
          productReviewsApi.stats(productId)
        ]);
        if (listRes?.success) setReviews(listRes.data || []);
        if (statsRes?.success) setStats(statsRes.data || { totalReviews: 0, averageRating: 0 });
        setMyReview({ rating: '', comment: '' });
      } else {
        throw new Error(res?.message || 'Failed to save review');
      }
    } catch (err) {
      console.error(err);
    } finally {
      setSubmittingReview(false);
    }
  };

  return (
    <SciFiBackground>
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: 'transparent',
          color: 'text.primary',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Navbar />
        
        <Container maxWidth="lg" sx={{ py: { xs: 4, md: 8 } }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(-1)}
            sx={{ 
              mb: 4, 
              color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)',
              borderRadius: compactRadius,
              '&:hover': {
                bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'
              }
            }}
          >
            {t('productDetails.backToMarketplace')}
          </Button>

          {loading ? (
            <Grid container spacing={4}>
              <Grid item xs={12} md={6}>
                <Skeleton variant="rectangular" height={400} sx={{ borderRadius }} />
              </Grid>
              <Grid item xs={12} md={6}>
                <Stack spacing={2}>
                  <Skeleton variant="text" height={60} width="80%" />
                  <Skeleton variant="text" height={30} width="40%" />
                  <Skeleton variant="rectangular" height={100} sx={{ borderRadius: compactRadius }} />
                  <Skeleton variant="rectangular" height={50} width="150px" sx={{ borderRadius: compactRadius }} />
                </Stack>
              </Grid>
            </Grid>
          ) : error ? (
            <Alert severity="error" variant="filled" sx={{ borderRadius: compactRadius }}>
              {error}
            </Alert>
          ) : product ? (
            <Stack spacing={6}>
              {/* Product Hero Section */}
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 3, md: 5 },
                  borderRadius,
                  bgcolor: theme.vitria?.input,
                  backdropFilter: 'blur(20px)',
                  border: '1px solid',
                  borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
                }}
              >
                <Grid container spacing={6}>
                  {/* Image Gallery */}
                  <Grid item xs={12} md={6}>
                    <Box
                      sx={{
                        position: 'relative',
                        borderRadius,
                        overflow: 'hidden',
                        bgcolor: theme.palette.mode === 'dark' ? '#000' : '#f5f5f5',
                        aspectRatio: '1/1',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: theme.shadows[10],
                      }}
                    >
                      {product.images && product.images.length > 0 ? (
                        <Box
                          component="img"
                          src={product.images[0].url || product.images[0].thumbnailUrl}
                          alt={product.name}
                          sx={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                          }}
                        />
                      ) : (
                        <LocalOfferIcon sx={{ fontSize: 80, color: 'text.disabled', opacity: 0.5 }} />
                      )}
                      {product.soldOut && (
                        <Box
                          sx={{
                            position: 'absolute',
                            inset: 0,
                            bgcolor: 'rgba(0,0,0,0.6)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            backdropFilter: 'blur(4px)',
                          }}
                        >
                          <Typography variant="h3" fontWeight={800} color="error.main" sx={{ transform: 'rotate(-15deg)', border: '4px solid', p: 2, borderRadius: compactRadius }}>
                            {t('productDetails.soldOut')}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Grid>

                  {/* Product Info */}
                  <Grid item xs={12} md={6}>
                    <Stack spacing={3} height="100%" justifyContent="center">
                      <Box>
                        <Stack direction="row" spacing={1} mb={2}>
                          {product.category && (
                            <Chip label={product.category} color="primary" variant="outlined" size="small" />
                          )}
                          {product.soldOut && (
                            <Chip label={t('productDetails.outOfStock')} color="error" size="small" />
                          )}
                        </Stack>
                        <Typography variant="h3" fontWeight={800} gutterBottom sx={{ lineHeight: 1.2, color: theme.palette.mode === 'dark' ? '#ffffff' : '#000000' }}>
                          {product.name}
                        </Typography>
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <Rating value={stats.averageRating || 0} readOnly precision={0.5} />
                          <Typography variant="body2" sx={{ color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)' }}>
                            {t('productDetails.reviewCount', { count: stats.totalReviews })}
                          </Typography>
                        </Stack>
                      </Box>

                      <Typography variant="h4" fontWeight={700} color="primary.main">
                        {formatPrice(product.price)}
                      </Typography>

                      <Typography variant="body1" sx={{ lineHeight: 1.8, color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)' }}>
                        {product.description}
                      </Typography>

                      <Divider sx={{ my: 2 }} />

                      <Stack direction="row" spacing={2} alignItems="center">
                        <TextField
                          label={t('productDetails.quantity')}
                          type="number"
                          value={quantity}
                          onChange={handleQuantityChange}
                          disabled={product.soldOut}
                          InputProps={{ inputProps: { min: 1 } }}
                          sx={{ width: 100 }}
                          size="medium"
                        />
                        <Button
                          variant="contained"
                          size="large"
                          fullWidth
                          startIcon={<ShoppingCartIcon />}
                          onClick={handleBuyNow}
                          disabled={product.soldOut}
                          sx={{ 
                            py: 1.5, 
                            borderRadius: compactRadius,
                            fontSize: '1.1rem',
                            fontWeight: 700,
                            boxShadow: theme.shadows[4],
                            textTransform: 'none'
                          }}
                        >
                          {product.soldOut ? t('productDetails.unavailable') : t('productDetails.buyNow')}
                        </Button>
                      </Stack>

                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 2 }}>
                        <VerifiedUserIcon fontSize="small" color="success" />
                        <Typography variant="caption" sx={{ color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)' }}>
                          {t('productDetails.secureTransaction')}
                        </Typography>
                      </Stack>
                    </Stack>
                  </Grid>
                </Grid>
              </Paper>

              {/* Reviews Section */}
              <Grid container spacing={4}>
                <Grid item xs={12} md={8}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 4,
                      borderRadius,
                      bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : '#ffffff',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid',
                      borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
                    }}
                  >
                    <Typography variant="h5" fontWeight={700} gutterBottom sx={{ color: theme.palette.mode === 'dark' ? '#ffffff' : '#000000' }}>
                      {t('productDetails.customerReviews')}
                    </Typography>
                    
                    {reviewsLoading ? (
                      <Stack spacing={2} mt={3}>
                        <Skeleton variant="rectangular" height={100} sx={{ borderRadius: compactRadius }} />
                        <Skeleton variant="rectangular" height={100} sx={{ borderRadius: compactRadius }} />
                      </Stack>
                    ) : reviews.length === 0 ? (
                      <Box sx={{ py: 6, textAlign: 'center' }}>
                        <Typography sx={{ color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)' }}>{t('productDetails.noReviews')}</Typography>
                      </Box>
                    ) : (
                      <Stack spacing={3} mt={3}>
                        {reviews.map((r) => (
                          <Paper
                            key={r._id}
                            elevation={0}
                            sx={{ 
                              p: 3, 
                              borderRadius: compactRadius, 
                              bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.01)' : '#fafafa',
                              border: '1px solid',
                              borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)'
                            }}
                          >
                            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                              <Stack direction="row" spacing={2} alignItems="center">
                                <Avatar sx={{ bgcolor: 'primary.main' }}>
                                  {r.userId?.username?.[0]?.toUpperCase() || 'U'}
                                </Avatar>
                                <Box>
                                  <Typography variant="subtitle2" fontWeight={700} sx={{ color: theme.palette.mode === 'dark' ? '#ffffff' : '#000000' }}>
                                    {r.userId?.username || t('productDetails.userFallback')}
                                  </Typography>
                                  <Rating value={r.rating} readOnly size="small" />
                                </Box>
                              </Stack>
                              <Typography variant="caption" sx={{ color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)' }}>
                                {new Date(r.createdAt).toLocaleDateString()}
                              </Typography>
                            </Stack>
                            {r.comment && (
                              <Typography variant="body2" sx={{ mt: 2, color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)' }}>
                                {r.comment}
                              </Typography>
                            )}
                          </Paper>
                        ))}
                      </Stack>
                    )}
                  </Paper>
                </Grid>

                <Grid item xs={12} md={4}>
                  {canReview ? (
                    <Paper
                      elevation={0}
                      sx={{
                        p: 3,
                        borderRadius,
                        bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : '#ffffff',
                        border: '1px solid',
                        borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
                        position: 'sticky',
                        top: 100,
                      }}
                    >
                      <Typography variant="h6" fontWeight={700} gutterBottom sx={{ color: theme.palette.mode === 'dark' ? '#ffffff' : '#000000' }}>
                        {t('productDetails.writeReview')}
                      </Typography>
                      <form onSubmit={submitReview}>
                        <Stack spacing={3} mt={2}>
                          <FormControl fullWidth>
                            <InputLabel>{t('productDetails.rating')}</InputLabel>
                            <Select
                              value={myReview.rating}
                              label={t('productDetails.rating')}
                              onChange={(e) => setMyReview((s) => ({ ...s, rating: e.target.value }))}
                              required
                            >
                              <MenuItem value="5">{t('productDetails.ratingExcellent')}</MenuItem>
                              <MenuItem value="4">{t('productDetails.ratingGood')}</MenuItem>
                              <MenuItem value="3">{t('productDetails.ratingAverage')}</MenuItem>
                              <MenuItem value="2">{t('productDetails.ratingPoor')}</MenuItem>
                              <MenuItem value="1">{t('productDetails.ratingTerrible')}</MenuItem>
                            </Select>
                          </FormControl>
                          
                          <TextField
                            label={t('productDetails.yourReview')}
                            multiline
                            rows={4}
                            value={myReview.comment}
                            onChange={(e) => setMyReview((s) => ({ ...s, comment: e.target.value }))}
                            placeholder={t('productDetails.sharePlaceholder')}
                          />

                          <Button
                            type="submit"
                            variant="contained"
                            disabled={submittingReview}
                            startIcon={submittingReview ? <CircularProgress size={20} /> : <SendIcon />}
                            sx={{ borderRadius: compactRadius, textTransform: 'none', fontWeight: 600 }}
                          >
                            {submittingReview ? t('productDetails.submitting') : t('productDetails.submitReview')}
                          </Button>
                          
                          <Typography variant="caption" align="center" sx={{ color: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)' }}>
                            {t('productDetails.verifiedBuyersOnly')}
                          </Typography>
                        </Stack>
                      </form>
                    </Paper>
                  ) : (
                    <Alert severity="info" variant="outlined" sx={{ borderRadius: compactRadius }}>
                      {t('productDetails.loginToReview')}
                    </Alert>
                  )}
                </Grid>
              </Grid>
            </Stack>
          ) : null}
        </Container>
      </Box>
    </SciFiBackground>
  );
};

export default ProductDetails;
